import { Component, inject, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';

import { ButtonComponent } from "../../components/button/button.component";
import TYPES from '../../constants/types';
import { GoogleButtonComponent } from "../../components/google-button/google-button.component";
import { InputComponent } from "../../components/input/input.component";
import { ActivatedRoute, Router, RouteReuseStrategy } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LanguagesSelectorComponent } from "../../components/languages-selector/languages-selector.component";
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { environment } from '../../../environments/environment';
import { CustomRouteReuseStrategy } from '../../util/custom-route-reuse-strategy';

@Component({
  selector: 'app-login',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    ButtonComponent,
    GoogleButtonComponent,
    InputComponent,
    TranslatePipe,
    LanguagesSelectorComponent,
    MatSnackBarModule
],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './login.component.scss'
})
export class LoginComponent {

  loginForm: FormGroup;
  BUTTON_TYPES: any;
  INFO_SCREEN: any;
  emailControl: UntypedFormControl;
  passwordControl: UntypedFormControl;
  inviteEmail: string | null;

  private snackBar:MatSnackBar = inject(MatSnackBar);
  private authService: AuthService = inject(AUTH_SERVICE);
  private translate: TranslateService = inject(TranslateService);

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private reuseStrategy: RouteReuseStrategy
  ) {
    
    this.inviteEmail = this.route.snapshot.queryParamMap.get('inviteEmail');

    this.emailControl = new UntypedFormControl(this.inviteEmail ?? '', [Validators.required, Validators.email]);
    this.passwordControl = new UntypedFormControl('', [Validators.required]);
    
    this.loginForm = this.formBuilder.group({
      email: this.emailControl,
      password: this.passwordControl
    });

    this.BUTTON_TYPES = TYPES.UI.BUTTON;
    this.INFO_SCREEN = TYPES.UI.INFO_SCREEN;

                
    //Delete the previous cache from historical screen
    const customReuseStrategy = this.reuseStrategy as CustomRouteReuseStrategy;
    customReuseStrategy.clearRoute('home/(home:historical)');
    customReuseStrategy.clearRoute('(primary:complete-account)');

  }

  async loginWithEmail() {
    if (this.loginForm.valid) {
      const error  = await this.authService.signInWithEmail(
        this.loginForm.value.email,
        this.loginForm.value.password
      );

      if(error) {
        if(error.message === 'Email not confirmed'){
          this.router.navigate([
            '/info',
            this.INFO_SCREEN.EMAIL_VERIFICATION_PENDING, 
            'info.verification_pending', 
            'info.verify_email', 
            'info.thanks_secure',
            'buttons.resend_email'
          ], { replaceUrl: true, queryParams: { email: this.loginForm.value.email } });
        }else if (error.message === 'Invalid login credentials'){
          const message = this.translate.instant('errors.invalid_credentials');
          const action = this.translate.instant('buttons.dismiss');
          this.snackBar.open(message, action);
        }

        console.error('Login with email failed:', error.message);
      }
    }else{
      this.loginForm.markAllAsTouched();
      this.loginForm.updateValueAndValidity();
    }
  }

  async loginWithGoogle() {
    const error = await this.authService.signInWithGoogle();
    if(error) {
      console.error('Login with Google failed:', error.message);
    }
  }

  register() {
    this.router.navigate(['/register'], { replaceUrl: true, queryParams: {inviteEmail: this.inviteEmail} });
  }

  forgotPassword() {
    this.router.navigate(['/forgot-password'], { replaceUrl: true });
  }

  getVersion(){
    return environment.VERSION;
  }

  view(page:string){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    if(page === 'policies'){
      this.router.navigate(['/policies']);
    }else if(page === 'terms'){
      this.router.navigate(['/terms']);
    }else if(page === 'about'){
      this.router.navigate(['/about']);
    }
  }

}
